import React, { useState, useEffect } from "react";
import "./AppHome.css";
import "../Admin/Admin.css";
import Header from "../Home/Header";
import { useNavigate } from "react-router-dom";
import { MdVisibility, MdEdit, MdDelete } from "react-icons/md";
import axios from "axios";

const API_URL =
  "https://gym-invoice-back.onrender.com/api/users";

// Hard-coded branch code.
// This will NOT be displayed in the frontend.
const BRANCH_CODE = "WB";

function AssistantManager() {
  const [users, setUsers] = useState([]);

  const [form, setForm] = useState({
    assistantName: "",
    username: "",
    password: "",
    role: "USER",
    active: true,
  });

  const [editIndex, setEditIndex] = useState(null);
  const [viewForm, setViewForm] = useState(null);

  const navigate = useNavigate();

  const [dialog, setDialog] = useState({
    show: false,
    type: "",
    message: "",
    onConfirm: null,
  });

  // =====================================================
  // CHECK LOGIN
  // =====================================================

  useEffect(() => {
    const role = sessionStorage.getItem("userRole");

    if (!role) {
      navigate("/");
    }
  }, [navigate]);

  // =====================================================
  // LOAD USERS
  // =====================================================

  useEffect(() => {
    fetchUsers();
  }, []);

  // =====================================================
  // FETCH USERS
  // =====================================================

  const fetchUsers = async () => {
    try {
      const response = await axios.get(API_URL);

      setUsers(response.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);

      setDialog({
        show: true,
        type: "error",
        message: "❌ Failed to load users.",
        onConfirm: null,
      });
    }
  };

  // =====================================================
  // HANDLE INPUT CHANGE
  // =====================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =====================================================
  // HANDLE STATUS
  // =====================================================

  const handleStatusChange = (e) => {
    setForm((prev) => ({
      ...prev,
      active: e.target.value === "true",
    }));
  };

  // =====================================================
  // ADD / UPDATE USER
  // =====================================================

  const handleAddOrUpdate = async () => {
    // =================================================
    // VALIDATION
    // =================================================

    if (!form.assistantName.trim()) {
      setDialog({
        show: true,
        type: "warning",
        message: "⚠️ Assistant Name is required.",
        onConfirm: null,
      });

      return;
    }

    if (!form.username.trim()) {
      setDialog({
        show: true,
        type: "warning",
        message: "⚠️ Username is required.",
        onConfirm: null,
      });

      return;
    }

    // Password required only when adding
    if (editIndex === null && !form.password.trim()) {
      setDialog({
        show: true,
        type: "warning",
        message: "⚠️ Password is required.",
        onConfirm: null,
      });

      return;
    }

    if (!form.role) {
      setDialog({
        show: true,
        type: "warning",
        message: "⚠️ Please select User Role.",
        onConfirm: null,
      });

      return;
    }

    try {
      // =================================================
      // UPDATE
      // =================================================

      if (editIndex !== null) {
        const userId =
          users[editIndex].id ||
          users[editIndex]._id;

        const updateData = {
          assistantName: form.assistantName,
          username: form.username,
          role: form.role,

          // Hard-coded branch code.
          // NOT shown in frontend.
          branchCode: BRANCH_CODE,

          active: form.active,
        };

        // Only send password if entered
        if (form.password.trim()) {
          updateData.password = form.password;
        }

        const response = await axios.put(
          `${API_URL}/${userId}`,
          updateData
        );

        const updated = [...users];

        updated[editIndex] = response.data;

        setUsers(updated);

        setEditIndex(null);

        resetForm();

        setDialog({
          show: true,
          type: "success",
          message: "✅ User updated successfully!",
          onConfirm: null,
        });
      }

      // =================================================
      // ADD
      // =================================================

      else {
        const newUser = {
          assistantName: form.assistantName,
          username: form.username,
          password: form.password,
          role: form.role,

          // Hard-coded branch code.
          // NOT shown in frontend.
          branchCode: BRANCH_CODE,

          active: form.active,
        };

        const response = await axios.post(
          API_URL,
          newUser
        );

        setUsers([
          ...users,
          response.data,
        ]);

        resetForm();

        setDialog({
          show: true,
          type: "success",
          message: "✅ User added successfully!",
          onConfirm: null,
        });
      }
    } catch (error) {
      console.error(
        editIndex !== null
          ? "Update user error:"
          : "Add user error:",
        error
      );

      const errorMessage =
        error.response?.data ||
        (
          editIndex !== null
            ? "❌ Failed to update user."
            : "❌ Failed to add user."
        );

      setDialog({
        show: true,
        type: "error",
        message: errorMessage,
        onConfirm: null,
      });
    }
  };

  // =====================================================
  // RESET FORM
  // =====================================================

  const resetForm = () => {
    setForm({
      assistantName: "",
      username: "",
      password: "",
      role: "USER",
      active: true,
    });

    setViewForm(null);
  };

  // =====================================================
  // VIEW USER
  // =====================================================

  const handleView = (user) => {
    setViewForm(user);

    setDialog({
      show: true,
      type: "success",
      message:
        `Viewing User:\n` +
        `Assistant Name: ${user.assistantName}\n` +
        `Username: ${user.username}\n` +
        `Role: ${user.role}\n` +
        `Status: ${
          user.active
            ? "Active"
            : "Inactive"
        }`,
      onConfirm: null,
    });
  };

  // =====================================================
  // EDIT USER
  // =====================================================

  const handleEdit = (user) => {
    const index = users.findIndex(
      (u) =>
        (u.id || u._id) ===
        (user.id || user._id)
    );

    setForm({
      assistantName:
        user.assistantName || "",

      username:
        user.username || "",

      password: "",

      role:
        user.role || "USER",

      active:
        user.active ?? true,
    });

    setEditIndex(index);
  };

  // =====================================================
  // DELETE USER
  // =====================================================

  const handleDelete = (user) => {
    const userId =
      user.id ||
      user._id;

    setDialog({
      show: true,
      type: "confirm",

      message:
        `⚠️ Are you sure you want to delete user "${user.username}"?`,

      onConfirm: async () => {
        try {
          await axios.delete(
            `${API_URL}/${userId}`
          );

          setUsers(
            users.filter(
              (u) =>
                (u.id || u._id) !==
                userId
            )
          );

          setDialog({
            show: true,
            type: "success",
            message:
              "✅ User deleted successfully!",
            onConfirm: null,
          });
        } catch (error) {
          console.error(
            "Delete user error:",
            error
          );

          setDialog({
            show: true,
            type: "error",
            message:
              "❌ Failed to delete user.",
            onConfirm: null,
          });
        }
      },
    });
  };

  // =====================================================
  // CANCEL EDIT
  // =====================================================

  const handleCancel = () => {
    setEditIndex(null);
    resetForm();
  };

  // =====================================================
  // LOGOUT
  // =====================================================

  const handleLogout = () => {
    navigate("/");
  };

  // =====================================================
  // VIEW
  // =====================================================

  return (
    <div className="dashboard">

      <Header />

      <div className="payment-container">

        <h2>
          👤 ASSISTANT MANAGEMENT
        </h2>

        {/* =================================================
            ADD / EDIT USER
        ================================================= */}

        <div className="payment-card">

          <h2>
            {editIndex !== null
              ? "📝 Edit Assistant"
              : "📝 Add Assistant"}
          </h2>

          {/* ASSISTANT NAME */}

          <input
            type="text"
            name="assistantName"
            value={form.assistantName}
            onChange={handleChange}
            placeholder="Assistant Name"
          />

          {/* USERNAME */}

          <input
            type="text"
            name="username"
            value={form.username}
            onChange={handleChange}
            placeholder="Username"
          />

          {/* PASSWORD */}

          <input
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            placeholder={
              editIndex !== null
                ? "Password (leave blank to keep current)"
                : "Password"
            }
          />

          {/* ROLE */}

          <select
            name="role"
            value={form.role}
            onChange={handleChange}
            className="duration-select"
          >
            <option value="USER">
              Assistant
            </option>

            <option value="ADMIN">
              Admin
            </option>
          </select>

          {/* STATUS */}

          <select
            name="active"
            value={
              form.active
                ? "true"
                : "false"
            }
            onChange={handleStatusChange}
            className="duration-select"
          >
            <option value="true">
              Active
            </option>

            <option value="false">
              Inactive
            </option>
          </select>

          {/* BUTTONS */}

          <div className="filter-buttons">

            <button
              className="pay-button"
              onClick={handleAddOrUpdate}
            >
              {editIndex !== null
                ? "💾 Update"
                : "➕ Add"}
            </button>

            {editIndex !== null && (
              <button
                type="button"
                className="pay-button"
                onClick={handleCancel}
              >
                ❌ Cancel
              </button>
            )}

          </div>

        </div>

        {/* =================================================
            USER TABLE
        ================================================= */}

        <div className="payment-table">

          <table>

            <thead>

              <tr>

                <th>
                  Assistant Name
                </th>

                <th>
                  Username
                </th>

                <th>
                  Role
                </th>

                <th>
                  Status
                </th>

                <th>
                  Actions
                </th>

              </tr>

            </thead>

            <tbody>

              {users.length === 0 ? (

                <tr>

                  <td
                    colSpan="5"
                    style={{
                      textAlign: "center",
                      padding: "15px",
                    }}
                  >
                    No users found.
                  </td>

                </tr>

              ) : (

                users.map((user) => (

                  <tr
                    key={
                      user.id ||
                      user._id
                    }
                  >

                    <td>
                      {user.assistantName}
                    </td>

                    <td>
                      {user.username}
                    </td>

                    <td>
                      {user.role}
                    </td>

                    <td>
                      {user.active
                        ? "Active"
                        : "Inactive"}
                    </td>

                    <td>

                      <div className="table-action-buttons">

                        {/* VIEW */}

                        <button
                          onClick={() =>
                            handleView(user)
                          }
                          className="action-btn view-btn"
                        >
                          <MdVisibility
                            size={20}
                          />

                          View
                        </button>

                        {/* EDIT */}

                        <button
                          onClick={() =>
                            handleEdit(user)
                          }
                          className="action-btn edit-btn"
                        >
                          <MdEdit
                            size={20}
                          />

                          Edit
                        </button>

                        {/* DELETE */}

                        <button
                          onClick={() =>
                            handleDelete(user)
                          }
                          className="action-btn delete-btn"
                        >
                          <MdDelete
                            size={20}
                          />

                          Delete
                        </button>

                      </div>

                    </td>

                  </tr>

                ))

              )}

            </tbody>

          </table>

        </div>

        {/* =================================================
            DIALOG
        ================================================= */}

        {dialog.show && (

          <div className="dialog-overlay">

            <div
              className={`dialog-box ${dialog.type}`}
            >

              <p
                style={{
                  whiteSpace: "pre-line",
                }}
              >
                {dialog.message}
              </p>

              <div className="dialog-buttons">

                {dialog.type === "confirm" ? (

                  <>

                    <button
                      onClick={() => {

                        dialog.onConfirm &&
                          dialog.onConfirm();

                        setDialog({
                          ...dialog,
                          show: false,
                        });

                      }}
                    >
                      Yes
                    </button>

                    <button
                      onClick={() =>
                        setDialog({
                          ...dialog,
                          show: false,
                        })
                      }
                    >
                      No
                    </button>

                  </>

                ) : (

                  <button
                    onClick={() =>
                      setDialog({
                        ...dialog,
                        show: false,
                      })
                    }
                  >
                    OK
                  </button>

                )}

              </div>

            </div>

          </div>

        )}

      </div>

    </div>
  );
}

export default AssistantManager;